


import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Info, Trophy, Zap } from "lucide-react";
import quotationsData from "../data/quotationsData";
import StatusStepper from "../components/modules/quotations/StatusStepper";
import VendorQuoteCard from "../components/modules/quotations/VendorQuoteCard";

function money(n) {
  return `₹${n.toLocaleString("en-IN")}`;
}

const statusStyles = {
  Draft: "bg-gray-50 text-gray-600 border-gray-200",
  Published: "bg-blue-50 text-blue-700 border-blue-200",
  "Quotations Received": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Under Evaluation": "bg-amber-50 text-amber-700 border-amber-200",
  "Quotation Selected": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Work Order Initiated": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "In Development": "bg-emerald-50 text-emerald-700 border-emerald-200",
  Completed: "bg-gray-50 text-gray-600 border-gray-200",
};

const filterOptions = ["All", "Lowest Price", "Fastest Delivery", "Best Rated", "Verified only"];

export default function QuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("All");
  const [comparing, setComparing] = useState([]);

  const enquiry = quotationsData.find((q) => q.id === id);

  const highlights = useMemo(() => {
    if (!enquiry || enquiry.quotes.length === 0) return null;
    const bestPrice = [...enquiry.quotes].sort((a, b) => a.total - b.total)[0];
    const fastest = [...enquiry.quotes].sort((a, b) => a.delivery - b.delivery)[0];
    const bestMatch = [...enquiry.quotes].sort((a, b) => b.rating - a.rating)[0];
    return { bestPrice, fastest, bestMatch };
  }, [enquiry]);

  const filteredQuotes = useMemo(() => {
    if (!enquiry) return [];
    const quotes = [...enquiry.quotes];
    switch (filter) {
      case "Lowest Price":
        return quotes.sort((a, b) => a.total - b.total);
      case "Fastest Delivery":
        return quotes.sort((a, b) => a.delivery - b.delivery);
      case "Best Rated":
        return quotes.sort((a, b) => b.rating - a.rating);
      case "Verified only":
        return quotes.filter((q) => q.verified);
      default:
        return quotes;
    }
  }, [enquiry, filter]);

  const badgeFor = (quote) => {
    if (!highlights) return null;
    if (quote.id === highlights.bestPrice.id) return "Best Price";
    if (quote.id === highlights.fastest.id) return "Fastest";
    if (quote.id === highlights.bestMatch.id) return "Best Match";
    return null;
  };

  const toggleCompare = (quoteId) => {
    setComparing((prev) =>
      prev.includes(quoteId) ? prev.filter((cid) => cid !== quoteId) : [...prev, quoteId]
    );
  };

  if (!enquiry) {
    return (
      <div className="h-40 flex items-center justify-center text-gray-400">
        Enquiry not found.
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate("/app/quotations")}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <h1 className="text-3xl font-extrabold text-gray-900">{enquiry.title}</h1>
      <p className="text-gray-500 mt-1 mb-5">
        {enquiry.id} · {enquiry.quotes.length} quotations received
      </p>

      {/* Enquiry summary card */}
      <div className="bg-white border border-gray-100 rounded-2xl p-5 mb-6">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-mono px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-200 text-gray-600">
              {enquiry.id}
            </span>
            <span
              className={`text-xs font-medium px-2.5 py-1 rounded-lg border ${
                statusStyles[enquiry.status] || "bg-gray-50 text-gray-600 border-gray-200"
              }`}
            >
              {enquiry.status}
            </span>
          </div>
          <p className="hidden sm:flex items-start gap-1.5 text-xs text-indigo-400 max-w-[260px] text-right">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            The system highlights strong candidates but the final decision is yours.
          </p>
        </div>

        <h2 className="font-bold text-gray-900 mb-1">{enquiry.title}</h2>
        <p className="text-sm text-gray-500 mb-4">{enquiry.description}</p>

        <div className="flex flex-wrap gap-2 mb-5">
          <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
            {enquiry.category}
          </span>
          <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
            {enquiry.industry}
          </span>
          <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
            Qty {enquiry.qty}
          </span>
          <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
            {money(enquiry.budgetMin)} – {money(enquiry.budgetMax)}
          </span>
          <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
            Target {enquiry.targetDate}
          </span>
        </div>

        <StatusStepper status={enquiry.status} />
      </div>

      {/* Highlight banners */}
      {highlights && (
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <div className="rounded-2xl p-4 text-white bg-gradient-to-r from-sky-400 to-blue-600">
            <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide opacity-90 mb-2">
              <Trophy className="w-3.5 h-3.5" />
              BEST PRICE
            </p>
            <p className="font-semibold text-sm">{highlights.bestPrice.vendor}</p>
            <p className="text-lg font-extrabold mt-0.5">{money(highlights.bestPrice.total)}</p>
          </div>
          <div className="rounded-2xl p-4 text-white bg-gradient-to-r from-purple-500 to-fuchsia-600">
            <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide opacity-90 mb-2">
              <Zap className="w-3.5 h-3.5" />
              FASTEST DELIVERY
            </p>
            <p className="font-semibold text-sm">{highlights.fastest.vendor}</p>
            <p className="text-lg font-extrabold mt-0.5">{highlights.fastest.delivery} days</p>
          </div>
          <div className="rounded-2xl p-4 text-white bg-gradient-to-r from-orange-400 to-pink-500">
            <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide opacity-90 mb-2">
              <Trophy className="w-3.5 h-3.5" />
              BEST OVERALL MATCH
            </p>
            <p className="font-semibold text-sm">{highlights.bestMatch.vendor}</p>
            <p className="text-lg font-extrabold mt-0.5">
              {highlights.bestMatch.rating}★ · {highlights.bestMatch.delivery}d
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-500 mr-1">Filter:</span>
        {filterOptions.map((f) => {
          const isActive = filter === f;
          return (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`text-sm font-medium px-4 py-1.5 rounded-full border transition ${
                isActive
                  ? "bg-indigo-600 border-indigo-600 text-white"
                  : "bg-white border-gray-200 text-gray-700 hover:border-indigo-300"
              }`}
            >
              {f}
            </button>
          );
        })}
      </div>

      {/* Vendor cards */}
      {filteredQuotes.length > 0 ? (
        <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-5">
          {filteredQuotes.map((quote, index) => (
            <VendorQuoteCard
              key={quote.id}
              quote={quote}
              badge={badgeFor(quote)}
              avatarIndex={index}
              isComparing={comparing.includes(quote.id)}
              onCompareToggle={() => toggleCompare(quote.id)}
            />
          ))}
        </div>
      ) : (
        <div className="h-40 flex items-center justify-center text-gray-400 border border-dashed border-gray-200 rounded-2xl">
          No quotations match this filter yet.
        </div>
      )}
    </div>
  );
}



// // // import { useState, useEffect, useMemo } from "react";
// // // import { useParams, useNavigate } from "react-router-dom";
// // // import { ArrowLeft, Info, Trophy, Zap, Loader2 } from "lucide-react";
// // // import enquiryService from "../service/enquiryService";
// // // import StatusStepper from "../components/modules/quotations/StatusStepper";
// // // import VendorQuoteCard from "../components/modules/quotations/VendorQuoteCard";

// // // function money(n) {
// // //   return `₹${n.toLocaleString("en-IN")}`;
// // // }

// // // const statusStyles = {
// // //   DRAFT: "bg-gray-50 text-gray-600 border-gray-200",
// // //   PUBLISHED: "bg-blue-50 text-blue-700 border-blue-200",
// // //   QUOTATIONS_RECEIVED: "bg-indigo-50 text-indigo-700 border-indigo-200",
// // //   UNDER_EVALUATION: "bg-amber-50 text-amber-700 border-amber-200",
// // //   QUOTATION_SELECTED: "bg-indigo-50 text-indigo-700 border-indigo-200",
// // //   WORK_ORDER_INITIATED: "bg-emerald-50 text-emerald-700 border-emerald-200",
// // //   IN_DEVELOPMENT: "bg-emerald-50 text-emerald-700 border-emerald-200",
// // //   COMPLETED: "bg-gray-50 text-gray-600 border-gray-200",
// // // };

// // // // Format status for display
// // // const formatStatus = (status) => {
// // //   if (!status) return "Draft";
// // //   return status
// // //     .split("_")
// // //     .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
// // //     .join(" ");
// // // };

// // // // Format budget range for display
// // // const formatBudgetRange = (budget) => {
// // //   if (!budget) return "Budget TBD";
  
// // //   const budgetMap = {
// // //     "BELOW_50000": "Below ₹50,000",
// // //     "BETWEEN_50000_AND_100000": "₹50,000 – ₹1,00,000",
// // //     "BETWEEN_100000_AND_500000": "₹1,00,000 – ₹5,00,000",
// // //     "ABOVE_500000": "Above ₹5,00,000",
// // //   };
  
// // //   return budgetMap[budget] || budget;
// // // };

// // // // Get status style
// // // const getStatusStyle = (status) => {
// // //   return statusStyles[status] || "bg-gray-50 text-gray-600 border-gray-200";
// // // };

// // // const filterOptions = ["All", "Lowest Price", "Fastest Delivery", "Best Rated", "Verified only"];

// // // export default function QuotationDetail() {
// // //   const { id } = useParams();
// // //   const navigate = useNavigate();
// // //   const [enquiry, setEnquiry] = useState(null);
// // //   const [loading, setLoading] = useState(true);
// // //   const [error, setError] = useState("");
// // //   const [filter, setFilter] = useState("All");
// // //   const [comparing, setComparing] = useState([]);

// // //   useEffect(() => {
// // //     if (id) {
// // //       fetchEnquiryDetail();
// // //     }
// // //   }, [id]);

// // // const fetchEnquiryDetail = async () => {
// // //   setLoading(true);
// // //   setError("");

// // //   try {
// // //     const response = await enquiryService.getEnquiryById(id);
// // //     console.log("Enquiry detail response:", response);

// // //     // ✅ Check if response is the data directly (not wrapped)
// // //     if (response && response.id) {
// // //       setEnquiry(response);
// // //     } else if (response.success && response.data) {
// // //       setEnquiry(response.data);
// // //     } else if (response.data && response.data.id) {
// // //       setEnquiry(response.data);
// // //     } else {
// // //       setError(response.message || "Enquiry not found.");
// // //       setEnquiry(null);
// // //     }
// // //   } catch (err) {
// // //     console.error("Error fetching enquiry detail:", err);
// // //     setError(err.message || "Failed to load enquiry details. Please try again.");
// // //     setEnquiry(null);
// // //   } finally {
// // //     setLoading(false);
// // //   }
// // // };

// // //   // For demo purposes - if no quotes in API response, use empty array
// // //   const quotes = enquiry?.quotes || enquiry?.quotations || [];

// // //   const highlights = useMemo(() => {
// // //     if (!enquiry || quotes.length === 0) return null;
// // //     const bestPrice = [...quotes].sort((a, b) => (a.total || a.price || 0) - (b.total || b.price || 0))[0];
// // //     const fastest = [...quotes].sort((a, b) => (a.delivery || 99) - (b.delivery || 99))[0];
// // //     const bestMatch = [...quotes].sort((a, b) => (b.rating || 0) - (a.rating || 0))[0];
// // //     return { bestPrice, fastest, bestMatch };
// // //   }, [enquiry, quotes]);

// // //   const filteredQuotes = useMemo(() => {
// // //     if (!quotes.length) return [];
// // //     const quotesList = [...quotes];
// // //     switch (filter) {
// // //       case "Lowest Price":
// // //         return quotesList.sort((a, b) => (a.total || a.price || 0) - (b.total || b.price || 0));
// // //       case "Fastest Delivery":
// // //         return quotesList.sort((a, b) => (a.delivery || 99) - (b.delivery || 99));
// // //       case "Best Rated":
// // //         return quotesList.sort((a, b) => (b.rating || 0) - (a.rating || 0));
// // //       case "Verified only":
// // //         return quotesList.filter((q) => q.verified);
// // //       default:
// // //         return quotesList;
// // //     }
// // //   }, [quotes, filter]);

// // //   const badgeFor = (quote) => {
// // //     if (!highlights) return null;
// // //     if (quote.id === highlights.bestPrice?.id) return "Best Price";
// // //     if (quote.id === highlights.fastest?.id) return "Fastest";
// // //     if (quote.id === highlights.bestMatch?.id) return "Best Match";
// // //     return null;
// // //   };

// // //   const toggleCompare = (quoteId) => {
// // //     setComparing((prev) =>
// // //       prev.includes(quoteId) ? prev.filter((cid) => cid !== quoteId) : [...prev, quoteId]
// // //     );
// // //   };

// // //   if (loading) {
// // //     return (
// // //       <div className="flex items-center justify-center h-64">
// // //         <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
// // //         <span className="ml-3 text-gray-600">Loading enquiry details...</span>
// // //       </div>
// // //     );
// // //   }

// // //   if (error || !enquiry) {
// // //     return (
// // //       <div>
// // //         <button
// // //           type="button"
// // //           onClick={() => navigate("/app/quotations")}
// // //           className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
// // //         >
// // //           <ArrowLeft className="w-4 h-4" />
// // //           Back
// // //         </button>
// // //         <div className="h-40 flex items-center justify-center text-red-500 border border-red-200 bg-red-50 rounded-2xl">
// // //           {error || "Enquiry not found."}
// // //         </div>
// // //       </div>
// // //     );
// // //   }

// // //   return (
// // //     <div>
// // //       <button
// // //         type="button"
// // //         onClick={() => navigate("/app/quotations")}
// // //         className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
// // //       >
// // //         <ArrowLeft className="w-4 h-4" />
// // //         Back to Quotations
// // //       </button>

// // //       <h1 className="text-3xl font-extrabold text-gray-900">{enquiry.projectName || "Untitled"}</h1>
// // //       <p className="text-gray-500 mt-1 mb-5">
// // //         {enquiry.id || enquiry.enquiryId} · {quotes.length} quotation{quotes.length === 1 ? "" : "s"} received
// // //       </p>

// // //       {/* Enquiry summary card */}
// // //       <div className="bg-white border border-gray-100 rounded-2xl p-5 mb-6">
// // //         <div className="flex items-start justify-between gap-4 mb-3">
// // //           <div className="flex flex-wrap items-center gap-2">
// // //             <span className="text-xs font-mono px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-200 text-gray-600">
// // //               {enquiry.id || enquiry.enquiryId || "N/A"}
// // //             </span>
// // //             <span
// // //               className={`text-xs font-medium px-2.5 py-1 rounded-lg border ${
// // //                 getStatusStyle(enquiry.status)
// // //               }`}
// // //             >
// // //               {formatStatus(enquiry.status)}
// // //             </span>
// // //           </div>
// // //           <p className="hidden sm:flex items-start gap-1.5 text-xs text-indigo-400 max-w-[260px] text-right">
// // //             <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
// // //             The system highlights strong candidates but the final decision is yours.
// // //           </p>
// // //         </div>

// // //         <h2 className="font-bold text-gray-900 mb-1">{enquiry.projectName || "Untitled"}</h2>
// // //         <p className="text-sm text-gray-500 mb-4">
// // //           {enquiry.detailedProjectDescription || enquiry.shortDescription || "No description provided"}
// // //         </p>

// // //         <div className="flex flex-wrap gap-2 mb-5">
// // //           <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
// // //             {enquiry.productCategory || enquiry.category || "General"}
// // //           </span>
// // //           <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
// // //             {enquiry.industrySector || enquiry.industry || "General"}
// // //           </span>
// // //           <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
// // //             Qty {enquiry.requiredQuantity || enquiry.qty || 0}
// // //           </span>
// // //           <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
// // //             {formatBudgetRange(enquiry.expectedBudgetRange)}
// // //           </span>
// // //           <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
// // //             Target {enquiry.targetDeliveryDate || "TBD"}
// // //           </span>
// // //         </div>

// // //         <StatusStepper status={enquiry.status} />
// // //       </div>

// // //       {/* Highlight banners - only if we have quotes */}
// // //       {highlights && quotes.length > 0 && (
// // //         <div className="grid sm:grid-cols-3 gap-4 mb-6">
// // //           <div className="rounded-2xl p-4 text-white bg-gradient-to-r from-sky-400 to-blue-600">
// // //             <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide opacity-90 mb-2">
// // //               <Trophy className="w-3.5 h-3.5" />
// // //               BEST PRICE
// // //             </p>
// // //             <p className="font-semibold text-sm">{highlights.bestPrice?.vendor || "N/A"}</p>
// // //             <p className="text-lg font-extrabold mt-0.5">{money(highlights.bestPrice?.total || 0)}</p>
// // //           </div>
// // //           <div className="rounded-2xl p-4 text-white bg-gradient-to-r from-purple-500 to-fuchsia-600">
// // //             <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide opacity-90 mb-2">
// // //               <Zap className="w-3.5 h-3.5" />
// // //               FASTEST DELIVERY
// // //             </p>
// // //             <p className="font-semibold text-sm">{highlights.fastest?.vendor || "N/A"}</p>
// // //             <p className="text-lg font-extrabold mt-0.5">{highlights.fastest?.delivery || "N/A"} days</p>
// // //           </div>
// // //           <div className="rounded-2xl p-4 text-white bg-gradient-to-r from-orange-400 to-pink-500">
// // //             <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide opacity-90 mb-2">
// // //               <Trophy className="w-3.5 h-3.5" />
// // //               BEST OVERALL MATCH
// // //             </p>
// // //             <p className="font-semibold text-sm">{highlights.bestMatch?.vendor || "N/A"}</p>
// // //             <p className="text-lg font-extrabold mt-0.5">
// // //               {highlights.bestMatch?.rating || "N/A"}★ · {highlights.bestMatch?.delivery || "N/A"}d
// // //             </p>
// // //           </div>
// // //         </div>
// // //       )}

// // //       {/* Filters - only show if we have quotes */}
// // //       {quotes.length > 0 && (
// // //         <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-2">
// // //           <span className="text-sm font-medium text-gray-500 mr-1">Filter:</span>
// // //           {filterOptions.map((f) => {
// // //             const isActive = filter === f;
// // //             return (
// // //               <button
// // //                 key={f}
// // //                 type="button"
// // //                 onClick={() => setFilter(f)}
// // //                 className={`text-sm font-medium px-4 py-1.5 rounded-full border transition ${
// // //                   isActive
// // //                     ? "bg-indigo-600 border-indigo-600 text-white"
// // //                     : "bg-white border-gray-200 text-gray-700 hover:border-indigo-300"
// // //                 }`}
// // //               >
// // //                 {f}
// // //               </button>
// // //             );
// // //           })}
// // //         </div>
// // //       )}

// // //       {/* Vendor cards */}
// // //       {filteredQuotes.length > 0 ? (
// // //         <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-5">
// // //           {filteredQuotes.map((quote, index) => (
// // //             <VendorQuoteCard
// // //               key={quote.id}
// // //               quote={quote}
// // //               badge={badgeFor(quote)}
// // //               avatarIndex={index}
// // //               isComparing={comparing.includes(quote.id)}
// // //               onCompareToggle={() => toggleCompare(quote.id)}
// // //             />
// // //           ))}
// // //         </div>
// // //       ) : (
// // //         <div className="h-40 flex items-center justify-center text-gray-400 border border-dashed border-gray-200 rounded-2xl">
// // //           {quotes.length === 0 ? "No quotations received yet." : "No quotations match this filter yet."}
// // //         </div>
// // //       )}
// // //     </div>
// // //   );
// // // }



// // import { useState, useEffect, useMemo } from "react";
// // import { useParams, useNavigate } from "react-router-dom";
// // import { ArrowLeft, Info, Trophy, Zap, Loader2 } from "lucide-react";
// // import enquiryService from "../service/enquiryService";
// // import quotationService from "../service/quotationService";
// // import StatusStepper from "../components/modules/quotations/StatusStepper";
// // import VendorQuoteCard from "../components/modules/quotations/VendorQuoteCard";

// // function money(n) {
// //   return `₹${n.toLocaleString("en-IN")}`;
// // }

// // const statusStyles = {
// //   DRAFT: "bg-gray-50 text-gray-600 border-gray-200",
// //   PUBLISHED: "bg-blue-50 text-blue-700 border-blue-200",
// //   QUOTATIONS_RECEIVED: "bg-indigo-50 text-indigo-700 border-indigo-200",
// //   UNDER_EVALUATION: "bg-amber-50 text-amber-700 border-amber-200",
// //   QUOTATION_SELECTED: "bg-indigo-50 text-indigo-700 border-indigo-200",
// //   WORK_ORDER_INITIATED: "bg-emerald-50 text-emerald-700 border-emerald-200",
// //   IN_DEVELOPMENT: "bg-emerald-50 text-emerald-700 border-emerald-200",
// //   COMPLETED: "bg-gray-50 text-gray-600 border-gray-200",
// // };

// // // Format status for display
// // const formatStatus = (status) => {
// //   if (!status) return "Draft";
// //   return status
// //     .split("_")
// //     .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
// //     .join(" ");
// // };

// // // Format budget range for display
// // const formatBudgetRange = (budget) => {
// //   if (!budget) return "Budget TBD";
  
// //   const budgetMap = {
// //     "BELOW_50000": "Below ₹50,000",
// //     "BETWEEN_50000_AND_100000": "₹50,000 – ₹1,00,000",
// //     "BETWEEN_100000_AND_500000": "₹1,00,000 – ₹5,00,000",
// //     "ABOVE_500000": "Above ₹5,00,000",
// //   };
  
// //   return budgetMap[budget] || budget;
// // };

// // // Get status style
// // const getStatusStyle = (status) => {
// //   return statusStyles[status] || "bg-gray-50 text-gray-600 border-gray-200";
// // };

// // // Map API quotation to VendorQuoteCard format
// // const mapQuotationToQuote = (quotation, index) => {
// //   return {
// //     id: quotation.quotationId || `q${index + 1}`,
// //     vendor: quotation.clientName || `Vendor ${index + 1}`,
// //     location: "APIC Centre", // Default location
// //     verified: quotation.adminStatus === "PENDING_REVIEW" || false,
// //     rating: 4.5, // Default rating since not in API
// //     yearsExp: 5, // Default experience
// //     delivery: quotation.estimatedDeliveryDays || 30,
// //     validity: 15, // Default validity
// //     payment: "50% Advance · 50% on Dispatch", // Default payment
// //     warranty: "6 months on manufacturing defects", // Default warranty
// //     material: Math.round(quotation.quotationAmount * 0.3),
// //     manufacturing: Math.round(quotation.quotationAmount * 0.4),
// //     tooling: Math.round(quotation.quotationAmount * 0.15),
// //     other: Math.round(quotation.quotationAmount * 0.05),
// //     gst: Math.round(quotation.quotationAmount * 0.10),
// //     total: quotation.quotationAmount,
// //     status: quotation.quotationStatus,
// //     adminStatus: quotation.adminStatus,
// //     message: quotation.message,
// //     version: quotation.version,
// //     selected: quotation.selected,
// //   };
// // };

// // const filterOptions = ["All", "Lowest Price", "Fastest Delivery", "Best Rated", "Verified only"];

// // export default function QuotationDetail() {
// //   const { id } = useParams();
// //   const navigate = useNavigate();
// //   const [enquiry, setEnquiry] = useState(null);
// //   const [quotations, setQuotations] = useState([]);
// //   const [loading, setLoading] = useState(true);
// //   const [error, setError] = useState("");
// //   const [filter, setFilter] = useState("All");
// //   const [comparing, setComparing] = useState([]);

// //   useEffect(() => {
// //     if (id) {
// //       fetchEnquiryDetail();
// //       fetchQuotations();
// //     }
// //   }, [id]);

// //   const fetchEnquiryDetail = async () => {
// //     try {
// //       const response = await enquiryService.getEnquiryById(id);
// //       console.log("Enquiry detail response:", response);

// //       if (response && response.id) {
// //         setEnquiry(response);
// //       } else if (response.success && response.data) {
// //         setEnquiry(response.data);
// //       } else if (response.data && response.data.id) {
// //         setEnquiry(response.data);
// //       } else {
// //         setError(response.message || "Enquiry not found.");
// //         setEnquiry(null);
// //       }
// //     } catch (err) {
// //       console.error("Error fetching enquiry detail:", err);
// //       setError(err.message || "Failed to load enquiry details.");
// //     }
// //   };

// //   const fetchQuotations = async () => {
// //     try {
// //       const response = await quotationService.getQuotationsByEnquiry(id);
// //       console.log("Quotations response:", response);

// //       let quotationsData = [];
// //       if (Array.isArray(response)) {
// //         quotationsData = response;
// //       } else if (response.data && Array.isArray(response.data)) {
// //         quotationsData = response.data;
// //       } else if (response.success && response.data && Array.isArray(response.data)) {
// //         quotationsData = response.data;
// //       } else {
// //         quotationsData = [];
// //       }
      
// //       setQuotations(quotationsData);
// //     } catch (err) {
// //       console.error("Error fetching quotations:", err);
// //       setQuotations([]);
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   // Convert API quotations to quote format
// //   const quotes = useMemo(() => {
// //     return quotations.map((q, index) => mapQuotationToQuote(q, index));
// //   }, [quotations]);

// //   const highlights = useMemo(() => {
// //     if (!enquiry || quotes.length === 0) return null;
// //     const bestPrice = [...quotes].sort((a, b) => (a.total || 0) - (b.total || 0))[0];
// //     const fastest = [...quotes].sort((a, b) => (a.delivery || 99) - (b.delivery || 99))[0];
// //     const bestMatch = [...quotes].sort((a, b) => (b.rating || 0) - (a.rating || 0))[0];
// //     return { bestPrice, fastest, bestMatch };
// //   }, [enquiry, quotes]);

// //   const filteredQuotes = useMemo(() => {
// //     if (!quotes.length) return [];
// //     const quotesList = [...quotes];
// //     switch (filter) {
// //       case "Lowest Price":
// //         return quotesList.sort((a, b) => (a.total || 0) - (b.total || 0));
// //       case "Fastest Delivery":
// //         return quotesList.sort((a, b) => (a.delivery || 99) - (b.delivery || 99));
// //       case "Best Rated":
// //         return quotesList.sort((a, b) => (b.rating || 0) - (a.rating || 0));
// //       case "Verified only":
// //         return quotesList.filter((q) => q.verified);
// //       default:
// //         return quotesList;
// //     }
// //   }, [quotes, filter]);

// //   const badgeFor = (quote) => {
// //     if (!highlights) return null;
// //     if (quote.id === highlights.bestPrice?.id) return "Best Price";
// //     if (quote.id === highlights.fastest?.id) return "Fastest";
// //     if (quote.id === highlights.bestMatch?.id) return "Best Match";
// //     return null;
// //   };

// //   const toggleCompare = (quoteId) => {
// //     setComparing((prev) =>
// //       prev.includes(quoteId) ? prev.filter((cid) => cid !== quoteId) : [...prev, quoteId]
// //     );
// //   };

// //   if (loading) {
// //     return (
// //       <div className="flex items-center justify-center h-64">
// //         <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
// //         <span className="ml-3 text-gray-600">Loading enquiry details...</span>
// //       </div>
// //     );
// //   }

// //   if (error || !enquiry) {
// //     return (
// //       <div>
// //         <button
// //           type="button"
// //           onClick={() => navigate("/app/quotations")}
// //           className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
// //         >
// //           <ArrowLeft className="w-4 h-4" />
// //           Back
// //         </button>
// //         <div className="h-40 flex items-center justify-center text-red-500 border border-red-200 bg-red-50 rounded-2xl">
// //           {error || "Enquiry not found."}
// //         </div>
// //       </div>
// //     );
// //   }

// //   return (
// //     <div>
// //       <button
// //         type="button"
// //         onClick={() => navigate("/app/quotations")}
// //         className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
// //       >
// //         <ArrowLeft className="w-4 h-4" />
// //         Back to Quotations
// //       </button>

// //       <h1 className="text-3xl font-extrabold text-gray-900">{enquiry.projectName || "Untitled"}</h1>
// //       <p className="text-gray-500 mt-1 mb-5">
// //         {enquiry.id || enquiry.enquiryId} · {quotes.length} quotation{quotes.length === 1 ? "" : "s"} received
// //       </p>

// //       {/* Enquiry summary card */}
// //       <div className="bg-white border border-gray-100 rounded-2xl p-5 mb-6">
// //         <div className="flex items-start justify-between gap-4 mb-3">
// //           <div className="flex flex-wrap items-center gap-2">
// //             <span className="text-xs font-mono px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-200 text-gray-600">
// //               {enquiry.id || enquiry.enquiryId || "N/A"}
// //             </span>
// //             <span
// //               className={`text-xs font-medium px-2.5 py-1 rounded-lg border ${
// //                 getStatusStyle(enquiry.status)
// //               }`}
// //             >
// //               {formatStatus(enquiry.status)}
// //             </span>
// //           </div>
// //           <p className="hidden sm:flex items-start gap-1.5 text-xs text-indigo-400 max-w-[260px] text-right">
// //             <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
// //             The system highlights strong candidates but the final decision is yours.
// //           </p>
// //         </div>

// //         <h2 className="font-bold text-gray-900 mb-1">{enquiry.projectName || "Untitled"}</h2>
// //         <p className="text-sm text-gray-500 mb-4">
// //           {enquiry.detailedProjectDescription || enquiry.shortDescription || "No description provided"}
// //         </p>

// //         <div className="flex flex-wrap gap-2 mb-5">
// //           <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
// //             {enquiry.productCategory || enquiry.category || "General"}
// //           </span>
// //           <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
// //             {enquiry.industrySector || enquiry.industry || "General"}
// //           </span>
// //           <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
// //             Qty {enquiry.requiredQuantity || enquiry.qty || 0}
// //           </span>
// //           <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
// //             {formatBudgetRange(enquiry.expectedBudgetRange)}
// //           </span>
// //           <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
// //             Target {enquiry.targetDeliveryDate || "TBD"}
// //           </span>
// //         </div>

// //         <StatusStepper status={enquiry.status} />
// //       </div>

// //       {/* Highlight banners - only if we have quotes */}
// //       {highlights && quotes.length > 0 && (
// //         <div className="grid sm:grid-cols-3 gap-4 mb-6">
// //           <div className="rounded-2xl p-4 text-white bg-gradient-to-r from-sky-400 to-blue-600">
// //             <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide opacity-90 mb-2">
// //               <Trophy className="w-3.5 h-3.5" />
// //               BEST PRICE
// //             </p>
// //             <p className="font-semibold text-sm">{highlights.bestPrice?.vendor || "N/A"}</p>
// //             <p className="text-lg font-extrabold mt-0.5">{money(highlights.bestPrice?.total || 0)}</p>
// //           </div>
// //           <div className="rounded-2xl p-4 text-white bg-gradient-to-r from-purple-500 to-fuchsia-600">
// //             <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide opacity-90 mb-2">
// //               <Zap className="w-3.5 h-3.5" />
// //               FASTEST DELIVERY
// //             </p>
// //             <p className="font-semibold text-sm">{highlights.fastest?.vendor || "N/A"}</p>
// //             <p className="text-lg font-extrabold mt-0.5">{highlights.fastest?.delivery || "N/A"} days</p>
// //           </div>
// //           <div className="rounded-2xl p-4 text-white bg-gradient-to-r from-orange-400 to-pink-500">
// //             <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide opacity-90 mb-2">
// //               <Trophy className="w-3.5 h-3.5" />
// //               BEST OVERALL MATCH
// //             </p>
// //             <p className="font-semibold text-sm">{highlights.bestMatch?.vendor || "N/A"}</p>
// //             <p className="text-lg font-extrabold mt-0.5">
// //               {highlights.bestMatch?.rating || "N/A"}★ · {highlights.bestMatch?.delivery || "N/A"}d
// //             </p>
// //           </div>
// //         </div>
// //       )}

// //       {/* Filters - only show if we have quotes */}
// //       {quotes.length > 0 && (
// //         <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-2">
// //           <span className="text-sm font-medium text-gray-500 mr-1">Filter:</span>
// //           {filterOptions.map((f) => {
// //             const isActive = filter === f;
// //             return (
// //               <button
// //                 key={f}
// //                 type="button"
// //                 onClick={() => setFilter(f)}
// //                 className={`text-sm font-medium px-4 py-1.5 rounded-full border transition ${
// //                   isActive
// //                     ? "bg-indigo-600 border-indigo-600 text-white"
// //                     : "bg-white border-gray-200 text-gray-700 hover:border-indigo-300"
// //                 }`}
// //               >
// //                 {f}
// //               </button>
// //             );
// //           })}
// //         </div>
// //       )}

// //       {/* Vendor cards */}
// //       {filteredQuotes.length > 0 ? (
// //         <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-5">
// //           {filteredQuotes.map((quote, index) => (
// //             <VendorQuoteCard
// //               key={quote.id}
// //               quote={quote}
// //               badge={badgeFor(quote)}
// //               avatarIndex={index}
// //               isComparing={comparing.includes(quote.id)}
// //               onCompareToggle={() => toggleCompare(quote.id)}
// //             />
// //           ))}
// //         </div>
// //       ) : (
// //         <div className="h-40 flex items-center justify-center text-gray-400 border border-dashed border-gray-200 rounded-2xl">
// //           {quotes.length === 0 ? "No quotations received yet." : "No quotations match this filter yet."}
// //         </div>
// //       )}
// //     </div>
// //   );
// // }






// import { useState, useEffect, useMemo } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { 
//   ArrowLeft, 
//   Info, 
//   Trophy, 
//   Zap, 
//   Loader2, 
//   Edit, 
//   X,
//   Save,
//   Calendar,
//   MapPin,
//   Users,
//   DollarSign,
//   FileText,
//   Clock
// } from "lucide-react";
// import enquiryService from "../service/enquiryService";
// import quotationService from "../service/quotationService";
// import StatusStepper from "../components/modules/quotations/StatusStepper";
// import VendorQuoteCard from "../components/modules/quotations/VendorQuoteCard";

// function money(n) {
//   return `₹${n.toLocaleString("en-IN")}`;
// }

// const statusStyles = {
//   DRAFT: "bg-gray-50 text-gray-600 border-gray-200",
//   PUBLISHED: "bg-blue-50 text-blue-700 border-blue-200",
//   QUOTATIONS_RECEIVED: "bg-indigo-50 text-indigo-700 border-indigo-200",
//   UNDER_EVALUATION: "bg-amber-50 text-amber-700 border-amber-200",
//   QUOTATION_SELECTED: "bg-indigo-50 text-indigo-700 border-indigo-200",
//   WORK_ORDER_INITIATED: "bg-emerald-50 text-emerald-700 border-emerald-200",
//   IN_DEVELOPMENT: "bg-emerald-50 text-emerald-700 border-emerald-200",
//   COMPLETED: "bg-gray-50 text-gray-600 border-gray-200",
// };

// // Format status for display
// const formatStatus = (status) => {
//   if (!status) return "Draft";
//   return status
//     .split("_")
//     .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
//     .join(" ");
// };

// // Format budget range for display
// const formatBudgetRange = (budget) => {
//   if (!budget) return "Budget TBD";
  
//   const budgetMap = {
//     "BELOW_50000": "Below ₹50,000",
//     "BETWEEN_50000_AND_100000": "₹50,000 – ₹1,00,000",
//     "BETWEEN_100000_AND_500000": "₹1,00,000 – ₹5,00,000",
//     "ABOVE_500000": "Above ₹5,00,000",
//   };
  
//   return budgetMap[budget] || budget;
// };

// // Get status style
// const getStatusStyle = (status) => {
//   return statusStyles[status] || "bg-gray-50 text-gray-600 border-gray-200";
// };

// // Map API quotation to VendorQuoteCard format
// const mapQuotationToQuote = (quotation, index) => {
//   return {
//     id: quotation.quotationId || `q${index + 1}`,
//     vendor: quotation.clientName || `Vendor ${index + 1}`,
//     location: "APIC Centre",
//     verified: quotation.adminStatus === "PENDING_REVIEW" || false,
//     rating: 4.5,
//     yearsExp: 5,
//     delivery: quotation.estimatedDeliveryDays || 30,
//     validity: 15,
//     payment: "50% Advance · 50% on Dispatch",
//     warranty: "6 months on manufacturing defects",
//     material: Math.round(quotation.quotationAmount * 0.3),
//     manufacturing: Math.round(quotation.quotationAmount * 0.4),
//     tooling: Math.round(quotation.quotationAmount * 0.15),
//     other: Math.round(quotation.quotationAmount * 0.05),
//     gst: Math.round(quotation.quotationAmount * 0.10),
//     total: quotation.quotationAmount,
//     status: quotation.quotationStatus,
//     adminStatus: quotation.adminStatus,
//     message: quotation.message,
//     version: quotation.version,
//     selected: quotation.selected,
//   };
// };

// const filterOptions = ["All", "Lowest Price", "Fastest Delivery", "Best Rated", "Verified only"];

// // Edit Modal Component
// function EditEnquiryModal({ enquiry, onClose }) {
//   const [formData, setFormData] = useState({
//     projectName: enquiry?.projectName || "",
//     requiredQuantity: enquiry?.requiredQuantity || "",
//     shortDescription: enquiry?.shortDescription || "",
//     detailedProjectDescription: enquiry?.detailedProjectDescription || "",
//     productCategory: enquiry?.productCategory || "",
//     industrySector: enquiry?.industrySector || "",
//     developmentStage: enquiry?.developmentStage || "",
//     expectedBudgetRange: enquiry?.expectedBudgetRange || "",
//     targetDeliveryDate: enquiry?.targetDeliveryDate || "",
//     preferredLocation: enquiry?.preferredLocation || "",
//     specialRequirements: enquiry?.specialRequirements || "",
//     additionalNotes: enquiry?.additionalNotes || "",
//   });

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData(prev => ({ ...prev, [name]: value }));
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     console.log("Updated enquiry data:", formData);
//     alert("⚠️ Edit functionality: API not available yet. Data logged to console.");
//     onClose();
//   };

//   if (!enquiry) return null;

//   return (
//     <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
//       <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
//         <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
//           <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
//             <Edit className="w-5 h-5 text-indigo-600" />
//             Edit Enquiry
//           </h2>
//           <button
//             onClick={onClose}
//             className="text-gray-400 hover:text-gray-600 transition"
//           >
//             <X className="w-6 h-6" />
//           </button>
//         </div>

//         <form onSubmit={handleSubmit} className="p-6 space-y-4">
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1.5">
//                 Project Name
//               </label>
//               <input
//                 type="text"
//                 name="projectName"
//                 value={formData.projectName}
//                 onChange={handleChange}
//                 className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
//                 placeholder="Enter project name"
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1.5">
//                 Required Quantity
//               </label>
//               <input
//                 type="number"
//                 name="requiredQuantity"
//                 value={formData.requiredQuantity}
//                 onChange={handleChange}
//                 className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
//                 placeholder="Enter quantity"
//               />
//             </div>
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1.5">
//               Short Description
//             </label>
//             <textarea
//               name="shortDescription"
//               value={formData.shortDescription}
//               onChange={handleChange}
//               rows={2}
//               className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
//               placeholder="Brief description"
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1.5">
//               Detailed Project Description
//             </label>
//             <textarea
//               name="detailedProjectDescription"
//               value={formData.detailedProjectDescription}
//               onChange={handleChange}
//               rows={3}
//               className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
//               placeholder="Detailed description of your project"
//             />
//           </div>

//           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1.5">
//                 Product Category
//               </label>
//               <input
//                 type="text"
//                 name="productCategory"
//                 value={formData.productCategory}
//                 onChange={handleChange}
//                 className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
//                 placeholder="e.g., Electronics, PCB"
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1.5">
//                 Industry Sector
//               </label>
//               <input
//                 type="text"
//                 name="industrySector"
//                 value={formData.industrySector}
//                 onChange={handleChange}
//                 className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
//                 placeholder="e.g., Manufacturing, Healthcare"
//               />
//             </div>
//           </div>

//           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1.5">
//                 Development Stage
//               </label>
//               <input
//                 type="text"
//                 name="developmentStage"
//                 value={formData.developmentStage}
//                 onChange={handleChange}
//                 className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
//                 placeholder="e.g., Design, Prototype"
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1.5">
//                 Expected Budget Range
//               </label>
//               <input
//                 type="text"
//                 name="expectedBudgetRange"
//                 value={formData.expectedBudgetRange}
//                 onChange={handleChange}
//                 className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
//                 placeholder="e.g., Below ₹50,000"
//               />
//             </div>
//           </div>

//           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1.5">
//                 Target Delivery Date
//               </label>
//               <input
//                 type="date"
//                 name="targetDeliveryDate"
//                 value={formData.targetDeliveryDate}
//                 onChange={handleChange}
//                 className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1.5">
//                 Preferred Location
//               </label>
//               <input
//                 type="text"
//                 name="preferredLocation"
//                 value={formData.preferredLocation}
//                 onChange={handleChange}
//                 className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
//                 placeholder="e.g., Visakhapatnam"
//               />
//             </div>
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1.5">
//               Special Requirements
//             </label>
//             <textarea
//               name="specialRequirements"
//               value={formData.specialRequirements}
//               onChange={handleChange}
//               rows={2}
//               className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
//               placeholder="Any special requirements"
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1.5">
//               Additional Notes
//             </label>
//             <textarea
//               name="additionalNotes"
//               value={formData.additionalNotes}
//               onChange={handleChange}
//               rows={2}
//               className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
//               placeholder="Any additional notes"
//             />
//           </div>

//           <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
//             <button
//               type="button"
//               onClick={onClose}
//               className="px-6 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-semibold hover:opacity-90 transition flex items-center gap-2"
//             >
//               <Save className="w-4 h-4" />
//               Save Changes
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// }

// export default function QuotationDetail() {
//   const { id } = useParams();
//   const navigate = useNavigate();
//   const [enquiry, setEnquiry] = useState(null);
//   const [quotations, setQuotations] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");
//   const [filter, setFilter] = useState("All");
//   const [comparing, setComparing] = useState([]);
//   const [showEditModal, setShowEditModal] = useState(false);

//   useEffect(() => {
//     if (id) {
//       fetchEnquiryDetail();
//       fetchQuotations();
//     }
//   }, [id]);

//   const fetchEnquiryDetail = async () => {
//     try {
//       const response = await enquiryService.getEnquiryById(id);
//       console.log("Enquiry detail response:", response);

//       if (response && response.id) {
//         setEnquiry(response);
//       } else if (response.success && response.data) {
//         setEnquiry(response.data);
//       } else if (response.data && response.data.id) {
//         setEnquiry(response.data);
//       } else {
//         setError(response.message || "Enquiry not found.");
//         setEnquiry(null);
//       }
//     } catch (err) {
//       console.error("Error fetching enquiry detail:", err);
//       setError(err.message || "Failed to load enquiry details.");
//     }
//   };

//   const fetchQuotations = async () => {
//     try {
//       const response = await quotationService.getQuotationsByEnquiry(id);
//       console.log("Quotations response:", response);

//       let quotationsData = [];
//       if (Array.isArray(response)) {
//         quotationsData = response;
//       } else if (response.data && Array.isArray(response.data)) {
//         quotationsData = response.data;
//       } else if (response.success && response.data && Array.isArray(response.data)) {
//         quotationsData = response.data;
//       } else {
//         quotationsData = [];
//       }
      
//       setQuotations(quotationsData);
//     } catch (err) {
//       console.error("Error fetching quotations:", err);
//       setQuotations([]);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Convert API quotations to quote format
//   const quotes = useMemo(() => {
//     return quotations.map((q, index) => mapQuotationToQuote(q, index));
//   }, [quotations]);

//   const highlights = useMemo(() => {
//     if (!enquiry || quotes.length === 0) return null;
//     const bestPrice = [...quotes].sort((a, b) => (a.total || 0) - (b.total || 0))[0];
//     const fastest = [...quotes].sort((a, b) => (a.delivery || 99) - (b.delivery || 99))[0];
//     const bestMatch = [...quotes].sort((a, b) => (b.rating || 0) - (a.rating || 0))[0];
//     return { bestPrice, fastest, bestMatch };
//   }, [enquiry, quotes]);

//   const filteredQuotes = useMemo(() => {
//     if (!quotes.length) return [];
//     const quotesList = [...quotes];
//     switch (filter) {
//       case "Lowest Price":
//         return quotesList.sort((a, b) => (a.total || 0) - (b.total || 0));
//       case "Fastest Delivery":
//         return quotesList.sort((a, b) => (a.delivery || 99) - (b.delivery || 99));
//       case "Best Rated":
//         return quotesList.sort((a, b) => (b.rating || 0) - (a.rating || 0));
//       case "Verified only":
//         return quotesList.filter((q) => q.verified);
//       default:
//         return quotesList;
//     }
//   }, [quotes, filter]);

//   const badgeFor = (quote) => {
//     if (!highlights) return null;
//     if (quote.id === highlights.bestPrice?.id) return "Best Price";
//     if (quote.id === highlights.fastest?.id) return "Fastest";
//     if (quote.id === highlights.bestMatch?.id) return "Best Match";
//     return null;
//   };

//   const toggleCompare = (quoteId) => {
//     setComparing((prev) =>
//       prev.includes(quoteId) ? prev.filter((cid) => cid !== quoteId) : [...prev, quoteId]
//     );
//   };

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center h-64">
//         <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
//         <span className="ml-3 text-gray-600">Loading enquiry details...</span>
//       </div>
//     );
//   }

//   if (error || !enquiry) {
//     return (
//       <div>
//         <button
//           type="button"
//           onClick={() => navigate("/app/quotations")}
//           className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
//         >
//           <ArrowLeft className="w-4 h-4" />
//           Back
//         </button>
//         <div className="h-40 flex items-center justify-center text-red-500 border border-red-200 bg-red-50 rounded-2xl">
//           {error || "Enquiry not found."}
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div>
//       <div className="flex items-center justify-between mb-4">
//         <button
//           type="button"
//           onClick={() => navigate("/app/quotations")}
//           className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
//         >
//           <ArrowLeft className="w-4 h-4" />
//           Back to Quotations
//         </button>

//         {/* Edit Button */}
//         <button
//           type="button"
//           onClick={() => setShowEditModal(true)}
//           className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:border-indigo-300 hover:text-indigo-600 transition"
//         >
//           <Edit className="w-4 h-4" />
//           Edit Enquiry
//         </button>
//       </div>

//       <h1 className="text-3xl font-extrabold text-gray-900">{enquiry.projectName || "Untitled"}</h1>
//       <p className="text-gray-500 mt-1 mb-5">
//         {enquiry.id || enquiry.enquiryId} · {quotes.length} quotation{quotes.length === 1 ? "" : "s"} received
//       </p>

//       {/* Enquiry summary card */}
//       <div className="bg-white border border-gray-100 rounded-2xl p-5 mb-6">
//         <div className="flex items-start justify-between gap-4 mb-3">
//           <div className="flex flex-wrap items-center gap-2">
//             <span className="text-xs font-mono px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-200 text-gray-600">
//               {enquiry.id || enquiry.enquiryId || "N/A"}
//             </span>
//             <span
//               className={`text-xs font-medium px-2.5 py-1 rounded-lg border ${
//                 getStatusStyle(enquiry.status)
//               }`}
//             >
//               {formatStatus(enquiry.status)}
//             </span>
//           </div>
//           <p className="hidden sm:flex items-start gap-1.5 text-xs text-indigo-400 max-w-[260px] text-right">
//             <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
//             The system highlights strong candidates but the final decision is yours.
//           </p>
//         </div>

//         <h2 className="font-bold text-gray-900 mb-1">{enquiry.projectName || "Untitled"}</h2>
//         <p className="text-sm text-gray-500 mb-4">
//           {enquiry.detailedProjectDescription || enquiry.shortDescription || "No description provided"}
//         </p>

//         <div className="flex flex-wrap gap-2 mb-5">
//           <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
//             {enquiry.productCategory || enquiry.category || "General"}
//           </span>
//           <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
//             {enquiry.industrySector || enquiry.industry || "General"}
//           </span>
//           <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
//             Qty {enquiry.requiredQuantity || enquiry.qty || 0}
//           </span>
//           <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
//             {formatBudgetRange(enquiry.expectedBudgetRange)}
//           </span>
//           <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700">
//             Target {enquiry.targetDeliveryDate || "TBD"}
//           </span>
//         </div>

//         <StatusStepper status={enquiry.status} />
//       </div>

//       {/* Highlight banners */}
//       {highlights && quotes.length > 0 && (
//         <div className="grid sm:grid-cols-3 gap-4 mb-6">
//           <div className="rounded-2xl p-4 text-white bg-gradient-to-r from-sky-400 to-blue-600">
//             <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide opacity-90 mb-2">
//               <Trophy className="w-3.5 h-3.5" />
//               BEST PRICE
//             </p>
//             <p className="font-semibold text-sm">{highlights.bestPrice?.vendor || "N/A"}</p>
//             <p className="text-lg font-extrabold mt-0.5">{money(highlights.bestPrice?.total || 0)}</p>
//           </div>
//           <div className="rounded-2xl p-4 text-white bg-gradient-to-r from-purple-500 to-fuchsia-600">
//             <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide opacity-90 mb-2">
//               <Zap className="w-3.5 h-3.5" />
//               FASTEST DELIVERY
//             </p>
//             <p className="font-semibold text-sm">{highlights.fastest?.vendor || "N/A"}</p>
//             <p className="text-lg font-extrabold mt-0.5">{highlights.fastest?.delivery || "N/A"} days</p>
//           </div>
//           <div className="rounded-2xl p-4 text-white bg-gradient-to-r from-orange-400 to-pink-500">
//             <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide opacity-90 mb-2">
//               <Trophy className="w-3.5 h-3.5" />
//               BEST OVERALL MATCH
//             </p>
//             <p className="font-semibold text-sm">{highlights.bestMatch?.vendor || "N/A"}</p>
//             <p className="text-lg font-extrabold mt-0.5">
//               {highlights.bestMatch?.rating || "N/A"}★ · {highlights.bestMatch?.delivery || "N/A"}d
//             </p>
//           </div>
//         </div>
//       )}

//       {/* Filters */}
//       {quotes.length > 0 && (
//         <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-2">
//           <span className="text-sm font-medium text-gray-500 mr-1">Filter:</span>
//           {filterOptions.map((f) => {
//             const isActive = filter === f;
//             return (
//               <button
//                 key={f}
//                 type="button"
//                 onClick={() => setFilter(f)}
//                 className={`text-sm font-medium px-4 py-1.5 rounded-full border transition ${
//                   isActive
//                     ? "bg-indigo-600 border-indigo-600 text-white"
//                     : "bg-white border-gray-200 text-gray-700 hover:border-indigo-300"
//                 }`}
//               >
//                 {f}
//               </button>
//             );
//           })}
//         </div>
//       )}

//       {/* Vendor cards */}
//       {filteredQuotes.length > 0 ? (
//         <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-5">
//           {filteredQuotes.map((quote, index) => (
//             <VendorQuoteCard
//               key={quote.id}
//               quote={quote}
//               badge={badgeFor(quote)}
//               avatarIndex={index}
//               isComparing={comparing.includes(quote.id)}
//               onCompareToggle={() => toggleCompare(quote.id)}
//             />
//           ))}
//         </div>
//       ) : (
//         <div className="h-40 flex items-center justify-center text-gray-400 border border-dashed border-gray-200 rounded-2xl">
//           {quotes.length === 0 ? "No quotations received yet." : "No quotations match this filter yet."}
//         </div>
//       )}

//       {/* Edit Modal */}
//       {showEditModal && (
//         <EditEnquiryModal
//           enquiry={enquiry}
//           onClose={() => setShowEditModal(false)}
//         />
//       )}
//     </div>
//   );
// }